import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import type { ConversationActionsFlow } from '../../../lib/use-conversation-actions';
import { Modal } from '../../Modal';
import { Button, Input, Typography } from '../ui';
import styles from './DeleteConversationConfirm.module.css';

// Rename a conversation. A dialog rather than an inline field: these rows live in a ~250px rail and inside a
// search dialog, neither of which has room to edit a title in place, and the wide rail's row is a Link — an
// inline field inside it would be a control inside a navigation target.
//
// The field is SEEDED with the current title (never left empty behind a placeholder) so a small correction is
// a small edit, which is the same rule the Maestro name editor follows. Save is blocked on a blank title:
// erasing the row's only label is not a rename.
export function RenameConversationDialog({ flow }: { flow: ConversationActionsFlow }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  // Reseed whenever a different conversation is targeted, so the previous one's title is never left behind.
  useEffect(() => {
    setDraft(flow.renameTarget?.title ?? '');
  }, [flow.renameTarget?.id, flow.renameTarget?.title]);

  if (!flow.renameTarget) return null;
  const trimmed = draft.trim();

  return (
    <Modal
      open
      onClose={flow.dismissRename}
      width={440}
      maxHeightPx={650}
      scrollBody
      title={t('conversations.rename.title')}
      labelledBy="rename-conversation-title"
      footer={
        <>
          <Button variant="outlined" onClick={flow.dismissRename} disabled={flow.busy}>
            {t('common.cancel')}
          </Button>
          <Button loading={flow.busy} disabled={!trimmed} onClick={() => void flow.confirmRename(trimmed)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Input
        label={t('conversations.rename.label')}
        value={draft}
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && trimmed && !flow.busy) void flow.confirmRename(trimmed);
        }}
      />
      {flow.error && (
        <Typography variant="p2" component="p" className={styles.error}>
          {t('conversations.rename.error')}
        </Typography>
      )}
    </Modal>
  );
}
